import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Briefcase, GraduationCap, Loader2, MapPin, Search, X } from 'lucide-react';
import { PRIMARY_ACTIVE_CITY } from '@/lib/geoScope';
import { zJednymMiejscem } from '@/lib/serpRead/miejsce';
import { czyPokazacOstatnie, czySzukac, obszarZMiejsca, wpisDlaObszaru, type RodzajMiejsca } from '@/lib/bliskoDo';
import { useOstatnieMiejsca, usePodpowiedziMiejsc, type Szukaj } from '@/hooks/useBliskoDo';
import type { SourcePostFilters } from '@/hooks/useSourcePosts';
import type { RecentPlace } from '@/types/savedSearch';

/** Ikona szkoły, pracy albo innego miejsca (makieta `serp-filter-panel.html`, `picon`). */
function IkonaMiejsca({ kind, className }: { kind?: RodzajMiejsca; className?: string }) {
  const Ikona = kind === 'school' ? GraduationCap : kind === 'work' ? Briefcase : MapPin;
  return <Ikona className={className} aria-hidden data-rodzaj={kind ?? 'other'} />;
}

/**
 * „BLISKO DO…” w wierszu Okolica (plan §1.67, §1.70, E4.3). Pole wyszukiwania z podpowiedziami
 * LocationIQ (przez funkcję `place-autocomplete`), JEDNO miejsce jako karta — pole wraca dopiero
 * po jej usunięciu — i „Ostatnio wybrane” pod pustym polem. Miejsce to obszar `kind: 'place'`
 * w `filters.areas` (A9), więc „Wyczyść” zabiera je razem z resztą lokalizacji; lista ostatnich
 * żyje osobno i zostaje.
 */
export function BliskoDo({ filters, onChange, szukaj }: { filters: SourcePostFilters; onChange: (next: SourcePostFilters) => void; szukaj?: Szukaj }) {
  const { t } = useTranslation();
  const [q, setQ] = useState('');
  const { places, status, laduje } = usePodpowiedziMiejsc(q, szukaj);
  const ostatnie = useOstatnieMiejsca();
  const miejsce = (filters.areas ?? []).find((a) => a.kind === 'place');
  const wybrane = miejsce && miejsce.kind === 'place' ? miejsce : null;

  const wybierz = (p: RecentPlace) => {
    onChange({ ...filters, areas: zJednymMiejscem(filters.areas, obszarZMiejsca(p)) });
    ostatnie.zapamietaj(p);
    setQ('');
  };
  const usunMiejsce = () => onChange({ ...filters, areas: zJednymMiejscem(filters.areas, null) });

  const szukam = czySzukac(q);
  const miasto = t(`search.area.miasto_miejscownik.${PRIMARY_ACTIVE_CITY}`, { defaultValue: PRIMARY_ACTIVE_CITY });
  const wpis = wybrane ? wpisDlaObszaru(ostatnie.lista, wybrane) : undefined;

  return (
    <div className="mt-4 space-y-2" data-testid="blisko-do">
      <p className="text-sm font-medium text-foreground">{t('search.filters.blisko.title')}</p>

      {wybrane ? (
        <div data-testid="blisko-karta" className="flex items-center gap-3 rounded-lg border border-primary bg-primary/5 px-3 py-2">
          <IkonaMiejsca kind={wpis?.kind} className="h-5 w-5 shrink-0 text-primary" />
          <span className="min-w-0 flex-1">
            <span className="block truncate text-sm font-semibold text-foreground">{wybrane.label}</span>
            <span className="block truncate text-xs text-muted-foreground">
              {[wpis?.address, t('search.filters.blisko.walk')].filter(Boolean).join(' · ')}
            </span>
          </span>
          <button
            type="button"
            aria-label={t('search.filters.blisko.remove', { name: wybrane.label })}
            onClick={usunMiejsce}
            className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full text-muted-foreground outline-none hover:text-destructive focus-visible:ring-2 focus-visible:ring-ring"
          >
            <X className="h-4 w-4" />
          </button>
        </div>
      ) : (
        <>
          <div className="relative">
            <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" aria-hidden />
            <input
              type="search"
              data-testid="blisko-pole"
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder={t('search.filters.blisko.placeholder')}
              aria-label={t('search.filters.blisko.title')}
              autoComplete="off"
              enterKeyHint="search"
              className="h-11 w-full rounded-lg border border-input bg-background pl-9 pr-9 text-base outline-none focus:border-primary focus-visible:ring-2 focus-visible:ring-ring md:h-10 md:text-sm"
            />
            {laduje && <Loader2 className="absolute right-3 top-1/2 h-4 w-4 -translate-y-1/2 animate-spin text-muted-foreground" aria-hidden />}
          </div>

          {czyPokazacOstatnie(q, ostatnie.lista, false) && (
            <div data-testid="blisko-ostatnie">
              <p className="mb-1.5 text-xs text-muted-foreground">{t('search.filters.blisko.recent')}</p>
              <div className="flex flex-wrap gap-2">
                {ostatnie.lista.map((p) => (
                  <span key={`${p.label}|${p.lat}|${p.lon}`} className="inline-flex h-10 max-w-full items-center rounded-full border bg-background md:h-9">
                    <button
                      type="button"
                      title={[p.label, p.address].filter(Boolean).join(', ')}
                      onClick={() => wybierz(p)}
                      className="flex min-w-0 items-center gap-1.5 truncate rounded-l-full pl-3 pr-1 text-base text-foreground outline-none focus-visible:ring-2 focus-visible:ring-ring md:text-sm"
                    >
                      <IkonaMiejsca kind={p.kind} className="h-3.5 w-3.5 shrink-0 text-muted-foreground" />
                      <span className="truncate">{p.label}</span>
                    </button>
                    <button
                      type="button"
                      aria-label={t('search.filters.blisko.remove_recent', { name: p.label })}
                      onClick={() => ostatnie.usun(p)}
                      className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full text-muted-foreground outline-none hover:text-foreground focus-visible:ring-2 focus-visible:ring-ring"
                    >
                      <X className="h-3.5 w-3.5" />
                    </button>
                  </span>
                ))}
              </div>
            </div>
          )}

          {/* Poprzednie podpowiedzi zostają, gdy przy kolejnym znaku leci nowe zapytanie (kręciołek w polu):
              lista nie znika na ≥300 ms przy każdym naciśnięciu klawisza. Pusta lista czeka na odpowiedź. */}
          {szukam && (!laduje || places.length > 0) && (
            <div className="space-y-1.5">
              <ul data-testid="blisko-wyniki" className="overflow-hidden rounded-lg border bg-card shadow-sm">
                {places.length ? (
                  places.map((p) => (
                    <li key={`${p.label}|${p.lat}|${p.lon}`} className="border-b last:border-b-0">
                      <button
                        type="button"
                        onClick={() => wybierz(p)}
                        className="flex w-full items-center gap-3 px-3 py-2.5 text-left outline-none hover:bg-muted focus-visible:bg-muted"
                      >
                        <IkonaMiejsca kind={p.kind} className="h-4 w-4 shrink-0 text-primary" />
                        <span className="min-w-0">
                          <span className="block truncate text-sm font-semibold text-foreground">{p.label}</span>
                          {p.address && <span className="block truncate text-xs text-muted-foreground">{p.address}</span>}
                        </span>
                      </button>
                    </li>
                  ))
                ) : (
                  <li className="px-3 py-2.5 text-sm text-muted-foreground" data-testid="blisko-brak">
                    {status === 'limited' || status === 'unavailable' ? t('search.filters.blisko.unavailable') : t('search.filters.blisko.no_results', { miasto })}
                  </li>
                )}
              </ul>
              <a
                href="https://locationiq.com"
                target="_blank"
                rel="noopener noreferrer"
                className="block text-right text-xs text-muted-foreground underline-offset-4 hover:underline"
              >
                {t('search.filters.blisko.attribution')}
              </a>
            </div>
          )}
        </>
      )}

      <p className="text-xs text-muted-foreground">
        {wybrane ? `${t('search.filters.blisko.hint_change')} ` : ''}
        {t('search.filters.blisko.hint')}
      </p>
    </div>
  );
}
